import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Plus, Trash2, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";

const FACILITY_OPTIONS = [
  { key: "dress_code", label_en: "Dress Code", label_mr: "ड्रेस कोड" },
  { key: "ground", label_en: "Ground", label_mr: "ग्राऊंड" },
  { key: "lecture", label_en: "Lecture", label_mr: "लेक्चर" },
  { key: "study_hall", label_en: "Study Hall", label_mr: "अभ्यासिका" },
  { key: "hostel", label_en: "Hostel", label_mr: "राहण्याची सोय" },
  { key: "mess", label_en: "Mess (Food)", label_mr: "जेवणाची सोय" },
];

// convert ISO -> YYYY-MM-DD for <input type="date">
const isoToDateInput = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

/**
 * Edit dialog usable by both admin and student.
 * - forStudent=true: shows fields a student is allowed to edit.
 * - forStudent=false: admin — full edit including programs, education, training, facilities & dates.
 */
export default function EditStudentDialog({ open, onOpenChange, student, forStudent = false, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: student.name || "", phone: student.phone || "", email: student.email || "",
    address: student.address || "", date_of_birth: student.date_of_birth || "", age: student.age || "",
    blood_group: student.blood_group || "", gender: student.gender || "",
    father_name: student.father_name || "", mother_name: student.mother_name || "",
    parent_phone: student.parent_phone || "", parent_occupation: student.parent_occupation || "",
    religion: student.religion || "", caste: student.caste || "", category: student.category || "",
    height: student.height || "", chest: student.chest || "", weight: student.weight || "",
    photo_url: student.photo_url || "",
    // admin-only fields
    program_ids: student.program_ids || (student.program_id ? [student.program_id] : []),
    education: (student.education && student.education.length > 0) ? student.education : [
      { class_name: "10th", year: "", percentage: "" },
      { class_name: "12th", year: "", percentage: "" },
    ],
    desired_post: student.desired_post || "Police Bharti",
    training_duration: student.training_duration || "12 months",
    facilities: student.facilities || [],
    enrollment_date: isoToDateInput(student.enrollment_date),
    expected_completion_date: isoToDateInput(student.expected_completion_date),
  }));
  const [programs, setPrograms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(student.photo_url || "");

  useEffect(() => {
    if (forStudent) return;
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, [forStudent]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "object" && e?.target ? e.target.value : e }));

  const updEdu = (i, k, v) => {
    const ed = [...form.education];
    ed[i] = { ...ed[i], [k]: v };
    setForm({ ...form, education: ed });
  };
  const addEdu = () => setForm({ ...form, education: [...form.education, { class_name: "", year: "", percentage: "" }] });
  const delEdu = (i) => setForm({ ...form, education: form.education.filter((_, idx) => idx !== i) });

  const toggleProgram = (pid) => {
    setForm((f) => {
      const ids = new Set(f.program_ids || []);
      if (ids.has(pid)) ids.delete(pid); else ids.add(pid);
      return { ...f, program_ids: Array.from(ids) };
    });
  };

  const toggleFacility = (key) => {
    setForm((f) => {
      const cur = new Set(f.facilities || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...f, facilities: Array.from(cur) };
    });
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) return toast.error("Photo must be under 1 MB");
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setForm((f) => ({ ...f, photo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!forStudent && (!form.program_ids || form.program_ids.length === 0)) {
      return toast.error("Please select at least one program");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.age === "" || payload.age === undefined || payload.age === null) {
        delete payload.age;
      } else {
        payload.age = parseInt(payload.age) || null;
      }
      payload.education = (payload.education || []).filter((x) => x.class_name);
      // don't send empty date strings
      if (!payload.enrollment_date) delete payload.enrollment_date;
      if (!payload.expected_completion_date) delete payload.expected_completion_date;
      await api.put(`/students/${student.id}`, payload);
      toast.success("Student updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="edit-student-dialog">
        <DialogHeader><DialogTitle>Edit Student / विद्यार्थी संपादित करा</DialogTitle></DialogHeader>

        {/* Personal */}
        <div className="flex items-start gap-6">
          <div className="flex flex-col items-center">
            <div className="w-24 h-28 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center overflow-hidden bg-slate-50">
              {photoPreview ? <img src={photoPreview} alt="student" className="w-full h-full object-cover" /> : <Camera size={24} className="text-slate-400" />}
            </div>
            <label className="mt-2 cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="edit-photo" />
              <span className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded text-xs font-medium hover:bg-slate-100">Change</span>
            </label>
          </div>
          <div className="flex-1 grid md:grid-cols-2 gap-3">
            <div><Label className="label-caps">Full Name</Label><Input data-testid="edit-name" value={form.name} onChange={upd("name")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Phone</Label><Input data-testid="edit-phone" maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} className="mt-1 h-10 font-mono" /></div>
            <div><Label className="label-caps">Email</Label><Input data-testid="edit-email" type="email" value={form.email} onChange={upd("email")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Date of Birth</Label><Input data-testid="edit-dob" type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Age</Label><Input type="number" value={form.age} onChange={upd("age")} className="mt-1 h-10" /></div>
            <div>
              <Label className="label-caps">Blood Group</Label>
              <Select value={form.blood_group} onValueChange={upd("blood_group")}>
                <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["A+","A-","B+","B-","O+","O-","AB+","AB-"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Gender</Label>
              <Select value={form.gender} onValueChange={upd("gender")}>
                <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="label-caps">Address</Label>
              <Textarea data-testid="edit-address" value={form.address} onChange={upd("address")} rows={2} className="mt-1" />
            </div>
          </div>
        </div>

        {/* Parents */}
        <div className="border-t pt-4 mt-4">
          <h4 className="font-semibold mb-2 text-sm">Parents / पालक</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="label-caps">Father&apos;s Name</Label><Input data-testid="edit-father" value={form.father_name} onChange={upd("father_name")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Mother&apos;s Name</Label><Input data-testid="edit-mother" value={form.mother_name} onChange={upd("mother_name")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Parent Phone</Label><Input maxLength={10} value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value.replace(/\D/g, "") })} className="mt-1 h-10 font-mono" /></div>
            <div><Label className="label-caps">Occupation</Label><Input value={form.parent_occupation} onChange={upd("parent_occupation")} className="mt-1 h-10" /></div>
          </div>
        </div>

        {/* Background & Physical */}
        <div className="border-t pt-4 mt-4">
          <h4 className="font-semibold mb-2 text-sm">Background & Physical</h4>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label className="label-caps">Religion</Label><Input value={form.religion} onChange={upd("religion")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Caste</Label><Input value={form.caste} onChange={upd("caste")} className="mt-1 h-10" /></div>
            <div>
              <Label className="label-caps">Category</Label>
              <Select value={form.category} onValueChange={upd("category")}>
                <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["Open","OBC","SC","ST","VJ/DT","NT","SBC","EWS"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-caps">Height (cm)</Label><Input value={form.height} onChange={upd("height")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Chest (cm)</Label><Input value={form.chest} onChange={upd("chest")} className="mt-1 h-10" /></div>
            <div><Label className="label-caps">Weight (kg)</Label><Input value={form.weight} onChange={upd("weight")} className="mt-1 h-10" /></div>
          </div>
        </div>

        {/* Admin-only sections */}
        {!forStudent && (
          <>
            {/* Programs */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-2 text-sm">Programs / प्रोग्राम्स</h4>
              <div className="grid md:grid-cols-2 gap-2" data-testid="edit-programs-list">
                {programs.map((p) => {
                  const checked = (form.program_ids || []).includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-start gap-3 p-3 rounded-md border-2 cursor-pointer transition ${checked ? "border-lime-500 bg-lime-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleProgram(p.id)} data-testid={`edit-program-${p.code || p.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold flex items-center gap-2">
                          {p.name}
                          {p.is_lifetime && <InfinityIcon size={12} className="text-lime-700" />}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {p.is_lifetime ? "Lifetime" : `${p.duration_months} months`} · ₹{(p.total_fee || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </label>
                  );
                })}
                {programs.length === 0 && <div className="text-sm text-slate-500 col-span-2">Loading programs…</div>}
              </div>
            </div>

            {/* Education */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm">Education / शैक्षणिक माहिती</h4>
                <Button type="button" size="sm" variant="outline" onClick={addEdu} data-testid="edit-add-edu">
                  <Plus size={14} className="mr-1" /> Add row
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left label-caps">Class</th>
                      <th className="px-3 py-2 text-left label-caps">Year</th>
                      <th className="px-3 py-2 text-left label-caps">%</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.education.map((row, i) => (
                      <tr key={i}>
                        <td className="p-2"><Input value={row.class_name || ""} onChange={(e) => updEdu(i, "class_name", e.target.value)} placeholder="10th / 12th / Degree" data-testid={`edit-edu-class-${i}`} /></td>
                        <td className="p-2"><Input value={row.year || ""} onChange={(e) => updEdu(i, "year", e.target.value)} placeholder="2023" /></td>
                        <td className="p-2"><Input value={row.percentage || ""} onChange={(e) => updEdu(i, "percentage", e.target.value)} placeholder="75" /></td>
                        <td className="p-2">
                          {form.education.length > 1 && (
                            <Button type="button" size="sm" variant="ghost" onClick={() => delEdu(i)}>
                              <Trash2 size={14} className="text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Training */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-2 text-sm">Training / प्रशिक्षण</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps">Desired Post</Label>
                  <Select value={form.desired_post} onValueChange={upd("desired_post")}>
                    <SelectTrigger className="mt-1 h-10" data-testid="edit-desired"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Police Bharti">पोलीस भरती</SelectItem>
                      <SelectItem value="Army Bharti">आर्मी भरती</SelectItem>
                      <SelectItem value="Both">Both / दोन्ही</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps">Training Duration</Label>
                  <Select value={form.training_duration} onValueChange={upd("training_duration")}>
                    <SelectTrigger className="mt-1 h-10" data-testid="edit-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3 months">3 months / ३ महिने</SelectItem>
                      <SelectItem value="6 months">6 months / ६ महिने</SelectItem>
                      <SelectItem value="12 months">12 months / १२ महिने</SelectItem>
                      <SelectItem value="until_selection">Until Bharti / भरती पर्यंत</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Label className="label-caps mt-4 mb-2 block">Our Facilities / आमच्या सुविधा</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="edit-facilities-list">
                {FACILITY_OPTIONS.map((f) => {
                  const checked = (form.facilities || []).includes(f.key);
                  return (
                    <label key={f.key} className={`flex items-center gap-3 p-2 rounded-md border-2 cursor-pointer transition ${checked ? "border-lime-500 bg-lime-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleFacility(f.key)} data-testid={`edit-facility-${f.key}`} />
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{f.label_en}</div>
                        <div className="text-xs text-slate-500">{f.label_mr}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Dates */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-2 text-sm">Enrollment Dates</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps">Enrollment Date</Label>
                  <Input type="date" value={form.enrollment_date} onChange={upd("enrollment_date")} className="mt-1 h-10" data-testid="edit-enrollment-date" />
                </div>
                <div>
                  <Label className="label-caps">Expected Completion</Label>
                  <Input type="date" value={form.expected_completion_date} onChange={upd("expected_completion_date")} className="mt-1 h-10" data-testid="edit-completion-date" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                💡 To change the fee amount, discount or installments, use the <strong>Reconfigure Fees</strong> button on the student detail page.
              </p>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
